import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getUser, getUsers, changeStaffLifecycle } from "../../actions/auth";
import { apiStaffLifecycleHistory } from "../../api/index.js";
import { Button, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { getUserFromLocalStorage } from "utilities/user";
import AppSelect from "../../components/ui/AppSelect";

// ── Helpers ────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const AVATAR_COLORS = ["#117a44", "#0d6efd", "#c8880a", "#6f42c1", "#0dcaf0", "#dc3545", "#20c997"];
const getAvatarColor = (name = "") => AVATAR_COLORS[(name || "").charCodeAt(0) % AVATAR_COLORS.length];

const ROLE_CONFIG = {
  ADMIN:       { bg: "#fce4ec", color: "#c62828", icon: "fa-user-shield" },
  SUPERADMIN:  { bg: "#ede7f6", color: "#4527a0", icon: "fa-crown" },
  MANAGER:     { bg: "#e3f2fd", color: "#1565c0", icon: "fa-user-tie" },
  AGENT:       { bg: "#e8f5e9", color: "#2e7d32", icon: "fa-headset" },
  DISPATCHER:  { bg: "#fff3e0", color: "#e65100", icon: "fa-truck" },
  SUPPORT:     { bg: "#e0f7fa", color: "#00695c", icon: "fa-life-ring" },
};

const RoleBadge = ({ role }) => {
  const cfg = ROLE_CONFIG[(role || "").toUpperCase()] || { bg: "#f0f0f0", color: "#555", icon: "fa-user" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: cfg.bg, color: cfg.color,
      padding: "3px 10px", borderRadius: 20,
      fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.03em",
    }}>
      <i className={`fas ${cfg.icon}`} style={{ fontSize: 9 }} />
      {role || "—"}
    </span>
  );
};

const STAFF_2FA_ROLES = ["SUPERUSER", "ADMIN", "SUPERVISOR"];

const TwoFAStatusBadge = ({ enabled, eligible }) => {
  if (!eligible) {
    return (
      <span style={{ fontSize: "0.78rem", color: "#9aacbf", fontWeight: 600 }} title="2FA is only available for admin, superuser, and supervisor accounts">
        —
      </span>
    );
  }
  if (enabled) {
    return (
      <span
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          background: "#e8f5e9", color: "#1b5e20",
          padding: "3px 10px", borderRadius: 20,
          fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em",
        }}
      >
        <i className="fas fa-shield-alt" style={{ fontSize: 9 }} />
        On
      </span>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        background: "#f4f6f9", color: "#6b7c93",
        padding: "3px 10px", borderRadius: 20,
        fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em",
      }}
    >
      <i className="fas fa-shield-alt" style={{ fontSize: 9, opacity: 0.6 }} />
      Off
    </span>
  );
};

// ── Staff lifecycle ────────────────────────────────────────────────────────
// ACTIVE / SUSPENDED / OFFBOARDED is DERIVED from the same two fields the
// server gates access on (isActive, deletedAt), so this directory can never
// show a green badge on an account that cannot sign in. Mirrors
// services/identity/staffLifecycle.js on the server.
const lifecycleState = (u) => {
  if (!u) return "ACTIVE";
  // The server sends its own answer; this fallback is for rows cached from
  // before that field existed, and keeps the two derivations identical.
  if (u.lifecycleState) return u.lifecycleState;
  if (u.deletedAt) return "OFFBOARDED";
  if (u.isActive === false) return "SUSPENDED";
  return "ACTIVE";
};

const STATUS_CONFIG = {
  ACTIVE:     { label: "Active",     bg: "#e8f5e9", color: "#1b5e20", icon: "fa-circle-check" },
  SUSPENDED:  { label: "Suspended",  bg: "#fff4e0", color: "#8a5a00", icon: "fa-ban" },
  OFFBOARDED: { label: "Offboarded", bg: "#f1f3f5", color: "#5a6270", icon: "fa-user-slash" },
};

const formatWhen = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

const StatusBadge = ({ state, snapshot }) => {
  const cfg = STATUS_CONFIG[state] || STATUS_CONFIG.ACTIVE;
  // The snapshot is the last action taken on this account, so the badge can
  // answer "since when, by whom and why" on hover instead of sending the
  // operator to a different screen.
  const detail =
    state === "ACTIVE" || !snapshot?.actedAt
      ? cfg.label
      : [
          `${cfg.label} ${formatWhen(snapshot.actedAt)}`,
          snapshot.actedByEmail ? `by ${snapshot.actedByEmail}` : "",
          snapshot.reason ? `reason: ${snapshot.reason}` : "",
        ].filter(Boolean).join(" · ");
  return (
    <span
      title={detail}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        background: cfg.bg, color: cfg.color,
        padding: "3px 10px", borderRadius: 20,
        fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em",
      }}
    >
      <i className={`fas ${cfg.icon}`} style={{ fontSize: 9 }} />
      {cfg.label}
    </span>
  );
};

// Only a Lusuku super user may change a colleague's access. The server enforces
// this twice (route guard and the write itself); the UI hides what it would
// refuse so nobody is offered a button that answers 403.
const isSuperUserProfile = (p) => {
  if (!p) return false;
  // userType is checked only when the cached profile carries it: this gate is
  // cosmetic (the server refuses everyone else twice over), and a profile
  // cached before userType was stored should not hide the controls from a real
  // super user. It is never the authority for the action itself.
  if (p.userType && p.userType !== "LUSUKU STAFF") return false;
  return (
    String(p.user_role || "").toUpperCase() === "SUPERUSER" || p.isSuperAdmin === true
  );
};

const MIN_REASON_LENGTH = 5;

// One dialog drives all four transitions. Each says plainly what it does to the
// person's access, what happens to their live sessions, and whether it can be
// undone, because the old dialog claimed a soft delete was irreversible.
const LIFECYCLE_ACTIONS = {
  SUSPEND: {
    label: "Suspend",
    icon: "fa-ban",
    colour: "#c8880a",
    btnClass: "pbi-btn-warn",
    title: "Suspend access",
    lead: "Blocks this person from signing in and signs them out of every device straight away.",
    points: [
      "They stay in the directory with their full record and history.",
      "Reversible at any time with Reinstate.",
    ],
    confirm: "Suspend access",
  },
  REINSTATE: {
    label: "Reinstate",
    icon: "fa-power-off",
    colour: "#117a44",
    btnClass: "pbi-btn-ok",
    title: "Reinstate access",
    lead: "Gives this person their platform access back with the role they already hold.",
    points: [
      "They sign in again from scratch, with whatever 2FA their account carries.",
      "Recorded against your name in the access history.",
    ],
    confirm: "Reinstate access",
  },
  OFFBOARD: {
    label: "Offboard",
    icon: "fa-user-slash",
    colour: "#dc3545",
    btnClass: "pbi-btn-del",
    title: "Offboard this staff member",
    lead: "For someone who has left Lusuku. Access stops immediately and the record leaves the directory.",
    points: [
      "Every live session is closed, their 2FA enrolment is cleared and any per-user permission overrides are removed.",
      "The account is kept, not deleted, so order history and audit trails stay intact.",
      "Reversible: tick Offboarded in the status filter to find them again and Restore.",
    ],
    confirm: "Offboard",
  },
  RESTORE: {
    label: "Restore",
    icon: "fa-trash-restore",
    colour: "#083860",
    btnClass: "pbi-btn-restore",
    title: "Restore to the directory",
    lead: "Brings this record back into the staff directory.",
    points: [
      "It comes back SUSPENDED, with no access. Use Reinstate afterwards to let them sign in again.",
      "Their 2FA enrolment was cleared at offboarding, so they will set it up again.",
    ],
    confirm: "Restore record",
  },
};

// One action set, rendered in both the desktop table and the mobile cards, so
// the two surfaces can never drift into offering different powers.
const LifecycleActions = ({ state, isSelf, canManage, onAction, onHistory }) => {
  if (!canManage) return null;
  const btn = (action, tooltip) => (
    <button
      type="button"
      className={`pbi-btn ${LIFECYCLE_ACTIONS[action].btnClass}`}
      title={tooltip}
      onClick={() => onAction(action)}
    >
      <i className={`fas ${LIFECYCLE_ACTIONS[action].icon}`} />
    </button>
  );
  return (
    <>
      {!isSelf && state === "ACTIVE" && btn("SUSPEND", "Suspend access")}
      {!isSelf && state === "SUSPENDED" && btn("REINSTATE", "Reinstate access")}
      {!isSelf && state !== "OFFBOARDED" && btn("OFFBOARD", "Offboard, this person has left")}
      {!isSelf && state === "OFFBOARDED" && btn("RESTORE", "Restore to the directory")}
      <button
        type="button"
        className="pbi-btn pbi-btn-history"
        title="Access history"
        onClick={onHistory}
      >
        <i className="fas fa-history" />
      </button>
    </>
  );
};

const STATUS_FILTER_OPTIONS = [
  { value: "",           label: "In the directory" },
  { value: "ACTIVE",     label: "Active only" },
  { value: "SUSPENDED",  label: "Suspended only" },
  { value: "OFFBOARDED", label: "Offboarded only" },
  { value: "ALL",        label: "Everyone, offboarded included" },
];

// ── Component ──────────────────────────────────────────────────────────────
const Users = () => {
  const user = getUserFromLocalStorage();
  const { authorizedUsers, isLoading, userCreated } = useSelector((state) => state.auth);

  const dispatch = useDispatch();
  const history = useHistory();

  const [searchTerm, setSearchTerm]             = useState("");
  const [filterRole, setFilterRole]             = useState("");
  const [filterStatus, setFilterStatus]         = useState("");
  const [sortField, setSortField]               = useState("name");
  const [sortDir, setSortDir]                   = useState("asc");
  const [showFilters, setShowFilters]           = useState(false);

  // ── Lifecycle dialog state ───────────────────────────────────────────────
  const [lifecycleTarget, setLifecycleTarget]   = useState(null);
  const [lifecycleAction, setLifecycleAction]   = useState(null);
  const [lifecycleReason, setLifecycleReason]   = useState("");
  const [lifecycleBusy, setLifecycleBusy]       = useState(false);

  // ── Access history dialog state ──────────────────────────────────────────
  const [historyFor, setHistoryFor]             = useState(null);
  const [historyEntries, setHistoryEntries]     = useState([]);
  const [historyLoading, setHistoryLoading]     = useState(false);
  const [historyError, setHistoryError]         = useState("");

  const canManageAccess = isSuperUserProfile(user?.result);

  // Offboarded staff are not in the default payload, so asking for them is a
  // refetch, not a client-side filter. The server grants it to super users only.
  const includeOffboarded = filterStatus === "OFFBOARDED" || filterStatus === "ALL";

  // Pulled out as primitives so the effect below can depend on the values that
  // actually change rather than on the profile object, which is rebuilt on
  // every render.
  const actorId = user?.result?._id;
  const actorRole = user?.result?.user_role;

  const refreshStaff = useCallback(
    () => dispatch(getUsers(actorId, actorRole, { includeOffboarded })),
    [dispatch, actorId, actorRole, includeOffboarded],
  );

  useEffect(() => {
    refreshStaff();
  }, [refreshStaff, userCreated]);

  const openLifecycle = (agentUser, action) => {
    setLifecycleTarget(agentUser);
    setLifecycleAction(action);
    setLifecycleReason("");
  };

  const closeLifecycle = () => {
    if (lifecycleBusy) return;
    setLifecycleTarget(null);
    setLifecycleAction(null);
    setLifecycleReason("");
  };

  const submitLifecycle = async () => {
    if (!lifecycleTarget || !lifecycleAction) return;
    if (lifecycleReason.trim().length < MIN_REASON_LENGTH) return;
    setLifecycleBusy(true);
    const ok = await dispatch(
      changeStaffLifecycle(lifecycleTarget._id, user?.result?._id, {
        action: lifecycleAction,
        reason: lifecycleReason.trim(),
      }),
    );
    setLifecycleBusy(false);
    if (ok) {
      setLifecycleTarget(null);
      setLifecycleAction(null);
      setLifecycleReason("");
      refreshStaff();
    }
  };

  const openHistory = async (agentUser) => {
    setHistoryFor(agentUser);
    setHistoryEntries([]);
    setHistoryError("");
    setHistoryLoading(true);
    try {
      const { data } = await apiStaffLifecycleHistory(agentUser._id, user?.result?._id);
      setHistoryEntries(data?.data?.entries || []);
    } catch (err) {
      setHistoryError(
        err?.response?.data?.message || "Could not load this staff member's access history.",
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const openStaffEdit = (agentId, role) => {
    dispatch(getUser(agentId, user?.result?._id, user?.result?.user_role));
    const hash =
      STAFF_2FA_ROLES.includes((role || "").toUpperCase()) ? "#two-factor-authentication" : "";
    history.push(`/admin/user-edit/${agentId}${hash}`);
  };

  // Base list — only LUSUKU STAFF
  const staffAgents = useMemo(
    () => (authorizedUsers || []).filter((a) => a?.user?.userType === "LUSUKU STAFF"),
    [authorizedUsers]
  );

  // Derived role list for dropdown
  const roleOptions = useMemo(() => {
    const roles = staffAgents.map((a) => a?.user?.user_role).filter(Boolean);
    return [...new Set(roles)].sort();
  }, [staffAgents]);

  // KPI breakdowns
  const adminCount      = useMemo(() => staffAgents.filter((a) => ["ADMIN","SUPERADMIN"].includes((a?.user?.user_role || "").toUpperCase())).length, [staffAgents]);
  const agentCount      = useMemo(() => staffAgents.filter((a) => !["ADMIN","SUPERADMIN"].includes((a?.user?.user_role || "").toUpperCase())).length, [staffAgents]);
  const statusCounts    = useMemo(() => {
    const counts = { ACTIVE: 0, SUSPENDED: 0, OFFBOARDED: 0 };
    staffAgents.forEach((a) => { counts[lifecycleState(a?.user)] += 1; });
    return counts;
  }, [staffAgents]);

  // Filter + sort pipeline
  const filteredAgents = useMemo(() => {
    let result = staffAgents.filter((a) => {
      const haystack = [a?.user?.name, a?.user?.email, a?.user?.user_role, a?.user?._id]
        .join(" ").toLowerCase();
      return haystack.includes(searchTerm.toLowerCase());
    });
    if (filterRole) result = result.filter((a) => a?.user?.user_role === filterRole);
    if (filterStatus && filterStatus !== "ALL") {
      result = result.filter((a) => lifecycleState(a?.user) === filterStatus);
    }
    result = [...result].sort((a, b) => {
      const valA = (a?.user?.[sortField] || "").toLowerCase();
      const valB = (b?.user?.[sortField] || "").toLowerCase();
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [staffAgents, searchTerm, filterRole, filterStatus, sortField, sortDir]);

  const activeFiltersCount = [filterRole, filterStatus].filter(Boolean).length;

  const clearAllFilters = () => {
    setFilterRole("");
    setFilterStatus("");
    setSearchTerm("");
    setSortField("name");
    setSortDir("asc");
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  // ── Restricted view ──────────────────────────────────────────────────────
  if (user?.result?.user_role === "CLIENT" || user?.result?.user_role === "CREATOR") {
    return (
      <div style={{ padding: 32, fontFamily: '"Segoe UI", system-ui, sans-serif' }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          background: "#fff3f4", border: "1.5px solid #f5c2c7",
          borderRadius: 10, padding: "16px 24px", color: "#c62828",
          fontSize: "0.95rem", fontWeight: 600,
        }}>
          <i className="fas fa-lock" style={{ fontSize: 20 }} />
          Restricted — Admins only
        </div>
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <>
      {isLoading && (
        <Spinner animation="grow" variant="success"
          style={{ position: "fixed", top: "50%", left: "50%", zIndex: 9999 }}
        />
      )}

      {/* ── Lifecycle confirmation: one dialog, four transitions ── */}
      <Modal show={!!lifecycleTarget} onHide={closeLifecycle} centered>
        {lifecycleTarget && lifecycleAction && (
          <>
            <Modal.Header
              closeButton
              style={{ borderBottom: `2px solid ${LIFECYCLE_ACTIONS[lifecycleAction].colour}` }}
            >
              <Modal.Title>
                <i
                  className={`fas ${LIFECYCLE_ACTIONS[lifecycleAction].icon}`}
                  style={{ color: LIFECYCLE_ACTIONS[lifecycleAction].colour, marginRight: 8 }}
                />
                {LIFECYCLE_ACTIONS[lifecycleAction].title}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: "0.88rem" }}>
              <div style={{ fontWeight: 700, color: "#1a2a3a", marginBottom: 2 }}>
                {lifecycleTarget.name || "This account"}
              </div>
              <div style={{ color: "#6b7c93", fontSize: "0.8rem", marginBottom: 12 }}>
                {lifecycleTarget.email} · {lifecycleTarget.user_role}
              </div>

              <p style={{ marginBottom: 8 }}>{LIFECYCLE_ACTIONS[lifecycleAction].lead}</p>
              <ul style={{ paddingLeft: 18, marginBottom: 16, color: "#4a5568" }}>
                {LIFECYCLE_ACTIONS[lifecycleAction].points.map((point) => (
                  <li key={point} style={{ marginBottom: 4 }}>{point}</li>
                ))}
              </ul>

              <label
                htmlFor="pbi-lifecycle-reason"
                style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#8a9ab0" }}
              >
                Reason (required)
              </label>
              <textarea
                id="pbi-lifecycle-reason"
                className="pbi-reason-input"
                rows={3}
                autoFocus
                value={lifecycleReason}
                onChange={(e) => setLifecycleReason(e.target.value)}
                placeholder="For example: left the company on 30 Sep, handover complete"
              />
              <div style={{ fontSize: "0.74rem", color: "#8a9ab0", marginTop: 6 }}>
                Stored in the access log against your name, with the date and this device.
                Minimum {MIN_REASON_LENGTH} characters.
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={closeLifecycle} disabled={lifecycleBusy}>
                Cancel
              </Button>
              <button
                type="button"
                className="pbi-modal-confirm"
                style={{ background: LIFECYCLE_ACTIONS[lifecycleAction].colour }}
                disabled={lifecycleBusy || lifecycleReason.trim().length < MIN_REASON_LENGTH}
                onClick={submitLifecycle}
              >
                {lifecycleBusy ? "Working…" : LIFECYCLE_ACTIONS[lifecycleAction].confirm}
              </button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* ── Access history ── */}
      <Modal show={!!historyFor} onHide={() => setHistoryFor(null)} centered size="lg">
        <Modal.Header closeButton style={{ borderBottom: "2px solid #083860" }}>
          <Modal.Title>
            <i className="fas fa-history" style={{ color: "#083860", marginRight: 8 }} />
            Access history
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.86rem", maxHeight: "60vh", overflowY: "auto" }}>
          {historyFor && (
            <div style={{ marginBottom: 14 }}>
              <span style={{ fontWeight: 700, color: "#1a2a3a" }}>{historyFor.name}</span>
              <span style={{ color: "#6b7c93" }}> · {historyFor.email}</span>
            </div>
          )}
          {historyLoading && <div style={{ color: "#8a9ab0" }}>Loading…</div>}
          {!historyLoading && historyError && (
            <div style={{ color: "#c62828" }}>{historyError}</div>
          )}
          {!historyLoading && !historyError && historyEntries.length === 0 && (
            <div style={{ color: "#9aacbf", fontStyle: "italic" }}>
              No access changes recorded for this account.
            </div>
          )}
          {!historyLoading && !historyError && historyEntries.length > 0 && (
            <table className="pbi-history-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>By</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {historyEntries.map((entry) => (
                  <tr key={entry._id}>
                    <td style={{ whiteSpace: "nowrap" }}>{formatWhen(entry.at)}</td>
                    <td style={{ fontWeight: 700 }}>
                      {LIFECYCLE_ACTIONS[entry.lifecycle]?.label ||
                        (entry.action === "USER_DELETED" ? "Offboard" : entry.action)}
                    </td>
                    <td>{entry.actorName || entry.actorEmail || "Unknown"}</td>
                    <td style={{ color: "#4a5568" }}>
                      {entry.reason || <span style={{ color: "#9aacbf", fontStyle: "italic" }}>none recorded</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setHistoryFor(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .pbi-page { background: #f4f6f9; min-height: 100vh; padding: 24px 20px; font-family: "Segoe UI", system-ui, sans-serif; }

        /* KPI Cards — 4 cols desktop, 2 cols mobile */
        .pbi-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
        .pbi-kpi-card {
          background: #fff; border-radius: 10px; padding: 18px 20px;
          box-shadow: 0 1px 6px rgba(0,0,0,0.07); border-top: 4px solid transparent;
          position: relative; overflow: hidden;
        }
        .pbi-kpi-label { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8a9ab0; margin-bottom: 6px; }
        .pbi-kpi-value { font-size: 2rem; font-weight: 800; line-height: 1; margin-bottom: 4px; }
        .pbi-kpi-sub   { font-size: 0.75rem; color: #8a9ab0; }
        .pbi-kpi-icon  { position: absolute; right: 18px; top: 50%; transform: translateY(-50%); opacity: 0.1; }

        /* Panel */
        .pbi-panel { background: #fff; border-radius: 10px; box-shadow: 0 1px 6px rgba(0,0,0,0.07); overflow: hidden; }
        .pbi-panel-header {
          background: linear-gradient(135deg, #1a2a3a 0%, #243447 100%);
          padding: 14px 16px;
          display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap;
        }
        .pbi-panel-title { color: #fff; font-size: 1.05rem; font-weight: 700; margin: 0; display: flex; align-items: center; gap: 8px; }
        .pbi-panel-title i { color: #5bc5f5; }
        .pbi-create-btn {
          display: inline-flex; align-items: center; gap: 7px;
          background: #117a44; color: #fff;
          padding: 8px 18px; border-radius: 8px; border: none;
          font-size: 0.82rem; font-weight: 700; cursor: pointer;
          transition: background 0.15s; text-decoration: none; white-space: nowrap;
        }
        .pbi-create-btn:hover { background: #0e6236; color: #fff; }

        /* Filter bar */
        .pbi-filter-bar { padding: 12px 16px; background: #fafbfc; border-bottom: 1px solid #f0f2f5; }
        .pbi-filter-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .pbi-filter-label { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: #8a9ab0; white-space: nowrap; }
        .pbi-search-wrap { position: relative; flex: 1; min-width: 0; }
        .pbi-search-wrap .pbi-search-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #9aacbf; font-size: 13px; pointer-events: none; }
        .pbi-search-wrap input { width: 100%; padding: 8px 34px 8px 35px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.83rem; outline: none; transition: border-color 0.2s; background: #fff; }
        .pbi-search-wrap input:focus { border-color: #5bc5f5; }
        .pbi-search-clear { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #9aacbf; cursor: pointer; padding: 2px; font-size: 12px; }
        .pbi-filter-select { padding: 6px 10px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.8rem; background: #fff; color: #2d3748; outline: none; transition: border-color 0.2s; cursor: pointer; height: 34px; }
        .pbi-filter-select:focus { border-color: #5bc5f5; }
        .pbi-filter-select.active { border-color: #5bc5f5; background: #f0f9ff; color: #1565c0; font-weight: 600; }
        .pbi-filter-toggle { height: 34px; padding: 0 14px; border: 1.5px solid #e4e8ef; border-radius: 8px; background: #fff; font-size: 0.8rem; font-weight: 600; color: #6b7c93; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: background 0.15s; white-space: nowrap; }
        .pbi-filter-toggle:hover, .pbi-filter-toggle.has-filters { background: #e8f4fd; border-color: #5bc5f5; color: #1565c0; }
        .pbi-filter-badge { background: #5bc5f5; color: #fff; width: 18px; height: 18px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.65rem; font-weight: 800; }
        .pbi-sort-btn { height: 34px; padding: 0 12px; border: 1.5px solid #e4e8ef; border-radius: 8px; background: #fff; color: #6b7c93; font-size: 0.8rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 5px; white-space: nowrap; }
        .pbi-sort-btn:hover, .pbi-sort-btn.active { background: #e8f4fd; border-color: #5bc5f5; color: #1565c0; }
        .pbi-filter-divider { width: 1px; height: 22px; background: #e4e8ef; margin: 0 2px; flex-shrink: 0; }
        .pbi-result-pill { display: flex; align-items: center; gap: 7px; background: #f0f2f5; color: #495057; padding: 6px 14px; border-radius: 20px; font-size: 0.78rem; font-weight: 600; border: 1px solid #e4e8ef; white-space: nowrap; }
        .pbi-result-pill .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .pbi-chip { display: inline-flex; align-items: center; gap: 5px; background: #e8f4fd; color: #1565c0; border: 1px solid #b3d9f7; padding: 3px 10px; border-radius: 20px; font-size: 0.74rem; font-weight: 600; }
        .pbi-chip-remove { background: none; border: none; cursor: pointer; padding: 0; color: #6aacdb; font-size: 11px; display: flex; align-items: center; margin-left: 2px; }
        .pbi-chip-remove:hover { color: #dc3545; }
        .pbi-clear-all { background: none; border: none; cursor: pointer; font-size: 0.74rem; font-weight: 600; color: #dc3545; padding: 3px 8px; border-radius: 6px; }
        .pbi-clear-all:hover { background: #f8d7da; }
        .pbi-chips-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; padding-top: 10px; }
        .pbi-expanded-filters { padding-top: 10px; border-top: 1px dashed #e4e8ef; margin-top: 8px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .pbi-filter-controls-row { display: contents; }

        /* Table — desktop */
        .pbi-table-scroll { overflow-x: auto; }
        table.pbi-table { width: 100%; border-collapse: collapse; }
        table.pbi-table thead tr { background: #f4f6f9; }
        table.pbi-table thead th { padding: 10px 14px; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8a9ab0; border-bottom: 2px solid #e9ecef; white-space: nowrap; border-top: none; }
        table.pbi-table thead th:first-child { padding-left: 20px; }
        table.pbi-table thead th.sortable { cursor: pointer; user-select: none; }
        table.pbi-table thead th.sortable:hover { color: #5bc5f5; }
        table.pbi-table thead th.sort-active { color: #1565c0; }
        .sort-arrow { margin-left: 4px; font-size: 0.65rem; }
        table.pbi-table tbody tr { border-left: 3px solid transparent; transition: background 0.12s, border-color 0.12s; }
        table.pbi-table tbody tr:hover { background: #f7fbff; border-left-color: #5bc5f5; }
        table.pbi-table tbody td { padding: 11px 14px; font-size: 0.82rem; color: #2d3748; border-bottom: 1px solid #f0f2f5; vertical-align: middle; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px; }
        table.pbi-table tbody td:first-child { padding-left: 20px; }
        table.pbi-table tbody tr:last-child td { border-bottom: none; }
        /* An account that cannot sign in should not read like one that can. */
        table.pbi-table tbody tr.pbi-row-inactive td { background: #fcfcfd; color: #6b7c93; }
        table.pbi-table tbody tr.pbi-row-inactive .pbi-name-text { color: #6b7c93; }

        /* Mobile card list — hidden on desktop */
        .pbi-card-list { display: none; padding: 12px; }
        .pbi-mobile-card {
          background: #fff; border-radius: 10px; padding: 14px 16px;
          border: 1px solid #e9ecef; border-left: 4px solid #5bc5f5;
          margin-bottom: 10px; display: flex; align-items: flex-start; gap: 12px;
        }
        .pbi-mobile-card-body { flex: 1; min-width: 0; }
        .pbi-mobile-card-name { font-weight: 700; font-size: 0.9rem; color: #1a2a3a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .pbi-mobile-card-email { font-size: 0.76rem; color: #6b7c93; margin: 2px 0 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .pbi-mobile-card-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 6px; }
        .pbi-mobile-card-actions { display: flex; gap: 6px; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end; max-width: 78px; }

        /* Avatar */
        .pbi-avatar { width: 34px; height: 34px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; font-size: 0.72rem; font-weight: 800; color: #fff; flex-shrink: 0; margin-right: 9px; }
        .pbi-avatar-lg { width: 42px; height: 42px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; font-size: 0.85rem; font-weight: 800; color: #fff; flex-shrink: 0; }
        .pbi-name-cell { display: flex; align-items: center; }
        .pbi-name-text { font-weight: 600; color: #1a2a3a; }
        .pbi-name-sub  { font-size: 0.7rem; color: #9aacbf; }

        /* ID pill */
        .pbi-id-pill { font-family: "SFMono-Regular", Consolas, monospace; font-size: 0.72rem; background: #f4f6f9; color: #6b7c93; padding: 2px 8px; border-radius: 6px; border: 1px solid #e4e8ef; letter-spacing: 0.02em; }

        /* Action buttons */
        .pbi-actions { display: flex; gap: 5px; }
        .pbi-btn { width: 30px; height: 30px; border-radius: 7px; border: 1.5px solid; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; cursor: pointer; transition: background 0.15s, color 0.15s; background: transparent; }
        .pbi-btn-edit { border-color: #c8d6e5; color: #6b7c93; }
        .pbi-btn-edit:hover { background: #fff3cd; border-color: #ffc107; color: #856404; }
        .pbi-btn-del  { border-color: #f5c2c7; color: #dc3545; }
        .pbi-btn-del:hover  { background: #f8d7da; }
        .pbi-btn-warn { border-color: #f2d9a6; color: #c8880a; }
        .pbi-btn-warn:hover { background: #fff4e0; }
        .pbi-btn-ok { border-color: #b7e0c6; color: #117a44; }
        .pbi-btn-ok:hover { background: #e8f5e9; }
        .pbi-btn-restore { border-color: #b9cbdc; color: #083860; }
        .pbi-btn-restore:hover { background: #e8f1f8; }
        .pbi-btn-history { border-color: #c8d6e5; color: #6b7c93; }
        .pbi-btn-history:hover { background: #e8f1f8; color: #083860; }

        /* Reason box + primary dialog button */
        .pbi-reason-input { width: 100%; margin-top: 6px; padding: 9px 11px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.85rem; outline: none; resize: vertical; font-family: inherit; }
        .pbi-reason-input:focus { border-color: #5bc5f5; }
        .pbi-modal-confirm { border: none; color: #fff; padding: 8px 18px; border-radius: 8px; font-size: 0.83rem; font-weight: 700; cursor: pointer; }
        .pbi-modal-confirm:disabled { opacity: 0.45; cursor: not-allowed; }

        /* Access history table: same type size in every column, no vertical rules */
        table.pbi-history-table { width: 100%; border-collapse: collapse; }
        table.pbi-history-table thead th { text-align: left; padding: 8px 10px; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8a9ab0; border-bottom: 2px solid #e9ecef; }
        table.pbi-history-table tbody td { padding: 9px 10px; font-size: 0.82rem; color: #2d3748; border-bottom: 1px solid #f0f2f5; vertical-align: top; }
        table.pbi-history-table tbody tr:last-child td { border-bottom: none; }

        .pbi-2fa-cell { white-space: normal !important; max-width: 140px !important; }
        .pbi-2fa-settings-link {
          display: inline-block; margin-top: 6px; padding: 0; border: none; background: none;
          font-size: 0.72rem; font-weight: 700; color: #117a44; cursor: pointer; text-decoration: underline;
        }
        .pbi-2fa-settings-link:hover { color: #0e6236; }

        /* Empty state */
        .pbi-empty { text-align: center; padding: 52px 20px; }
        .pbi-empty-icon { font-size: 36px; margin-bottom: 12px; opacity: 0.22; color: #8a9ab0; }
        .pbi-empty-msg { font-size: 0.88rem; color: #9aacbf; font-style: italic; }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .pbi-page { padding: 16px 12px; }
          .pbi-kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 16px; }
          .pbi-kpi-card { padding: 14px 14px; }
          .pbi-kpi-value { font-size: 1.6rem; }
          .pbi-kpi-icon { display: none; }
          .pbi-panel-header { padding: 12px 14px; }
          .pbi-panel-title { font-size: 0.95rem; }
          .pbi-filter-bar { padding: 10px 12px; }
          .pbi-filter-divider { display: none; }
          .pbi-filter-label { display: none; }
          /* Search full-width, controls on second row */
          .pbi-filter-row { flex-direction: column; align-items: stretch; gap: 8px; }
          .pbi-search-wrap { flex: unset; width: 100%; }
          .pbi-filter-controls-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
          .pbi-filter-select { flex: 1; min-width: 0; }
          .pbi-result-pill-wrap { margin-left: auto; }
          /* Hide table, show card list */
          .pbi-table-scroll { display: none; }
          .pbi-card-list { display: block; }
        }

        @media (max-width: 480px) {
          .pbi-kpi-value { font-size: 1.4rem; }
          .pbi-kpi-label { font-size: 0.65rem; }
          .pbi-kpi-sub { display: none; }
        }
      `}</style>

      <div className="pbi-page">

        {/* ── KPI Cards ── */}
        <div className="pbi-kpi-grid">
          <div className="pbi-kpi-card" style={{ borderTopColor: "#0d6efd" }}>
            <div className="pbi-kpi-icon"><i className="fas fa-users" style={{ fontSize: 52, color: "#0d6efd" }} /></div>
            <div className="pbi-kpi-label">Total Staff</div>
            <div className="pbi-kpi-value" style={{ color: "#0d6efd" }}>{staffAgents.length}</div>
            <div className="pbi-kpi-sub">
              {statusCounts.ACTIVE} active, {statusCounts.SUSPENDED} suspended
              {statusCounts.OFFBOARDED > 0 ? `, ${statusCounts.OFFBOARDED} offboarded` : ""}
            </div>
          </div>
          <div className="pbi-kpi-card" style={{ borderTopColor: "#117a44" }}>
            <div className="pbi-kpi-icon"><i className="fas fa-headset" style={{ fontSize: 52, color: "#117a44" }} /></div>
            <div className="pbi-kpi-label">Agents / Ops</div>
            <div className="pbi-kpi-value" style={{ color: "#117a44" }}>{agentCount}</div>
            <div className="pbi-kpi-sub">Non-admin staff</div>
          </div>
          <div className="pbi-kpi-card" style={{ borderTopColor: "#c8880a" }}>
            <div className="pbi-kpi-icon"><i className="fas fa-user-shield" style={{ fontSize: 52, color: "#c8880a" }} /></div>
            <div className="pbi-kpi-label">Admins</div>
            <div className="pbi-kpi-value" style={{ color: "#c8880a" }}>{adminCount}</div>
            <div className="pbi-kpi-sub">Admin &amp; Super-admin</div>
          </div>
          <div className="pbi-kpi-card" style={{ borderTopColor: "#6f42c1" }}>
            <div className="pbi-kpi-icon"><i className="fas fa-id-badge" style={{ fontSize: 52, color: "#6f42c1" }} /></div>
            <div className="pbi-kpi-label">Roles in use</div>
            <div className="pbi-kpi-value" style={{ color: "#6f42c1" }}>{roleOptions.length}</div>
            <div className="pbi-kpi-sub">Distinct role types</div>
          </div>
        </div>

        {/* ── Main Panel ── */}
        <div className="pbi-panel">

          {/* Dark header */}
          <div className="pbi-panel-header">
            <div className="pbi-panel-title">
              <i className="fas fa-users" />
              Lusuku Staff Directory
            </div>
            <button className="pbi-create-btn" onClick={() => history.push("/admin/user-form/")}>
              <i className="fas fa-user-plus" />
              Create Agent
            </button>
          </div>

          {/* Filter bar */}
          <div className="pbi-filter-bar">
            <div className="pbi-filter-row">
              {/* Search */}
              <div className="pbi-search-wrap">
                <i className="fas fa-search pbi-search-icon" />
                <input
                  type="text"
                  placeholder="Search staff by name, email, role…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="pbi-search-clear" onClick={() => setSearchTerm("")}>
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>

              <div className="pbi-filter-controls-row">
                <div className="pbi-filter-divider" />

                {/* Filters toggle */}
                <button
                  className={`pbi-filter-toggle${activeFiltersCount > 0 ? " has-filters" : ""}`}
                  onClick={() => setShowFilters((v) => !v)}
                >
                  <i className="fas fa-sliders-h" />
                  Filters
                  {activeFiltersCount > 0 && <span className="pbi-filter-badge">{activeFiltersCount}</span>}
                  <i className={`fas fa-chevron-${showFilters ? "up" : "down"}`} style={{ fontSize: 10, marginLeft: 2 }} />
                </button>

                <div className="pbi-filter-divider" />

                {/* Sort */}
                <span className="pbi-filter-label">Sort</span>
                <AppSelect
                  className="pbi-filter-select"
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  style={{ minWidth: 120 }}
                >
                  <option value="name">Name</option>
                  <option value="email">Email</option>
                  <option value="user_role">Role</option>
                </AppSelect>
                <button
                  className={`pbi-sort-btn${sortDir === "desc" ? " active" : ""}`}
                  onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")}
                  title="Toggle sort direction"
                >
                  <i className={`fas fa-sort-amount-${sortDir === "asc" ? "up" : "down"}`} />
                  {sortDir === "asc" ? "A → Z" : "Z → A"}
                </button>

                {/* Result pill */}
                <div className="pbi-result-pill-wrap" style={{ marginLeft: "auto" }}>
                  <div className="pbi-result-pill">
                    <span className="dot" style={{ background: "#0d6efd" }} />
                    {filteredAgents.length} staff member{filteredAgents.length !== 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            </div>

            {/* Expanded filters */}
            {showFilters && (
              <div className="pbi-expanded-filters">
                <span className="pbi-filter-label">Role</span>
                <AppSelect
                  className={`pbi-filter-select${filterRole ? " active" : ""}`}
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  style={{ minWidth: 160 }}
                >
                  <option value="">All Roles</option>
                  {roleOptions.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </AppSelect>

                <span className="pbi-filter-label">Status</span>
                <AppSelect
                  className={`pbi-filter-select${filterStatus ? " active" : ""}`}
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{ minWidth: 200 }}
                  title="Offboarded staff are kept out of the directory by default"
                >
                  {STATUS_FILTER_OPTIONS.map((o) => (
                    <option key={o.value || "default"} value={o.value}>{o.label}</option>
                  ))}
                </AppSelect>
                {activeFiltersCount > 0 && (
                  <button className="pbi-clear-all" onClick={clearAllFilters}>
                    <i className="fas fa-times-circle" style={{ marginRight: 4 }} />
                    Clear all
                  </button>
                )}
              </div>
            )}

            {/* Active chips */}
            {activeFiltersCount > 0 && (
              <div className="pbi-chips-row">
                <span className="pbi-filter-label">Active:</span>
                {filterRole && (
                  <span className="pbi-chip">
                    <i className="fas fa-user-tag" style={{ fontSize: 10 }} />
                    {filterRole}
                    <button className="pbi-chip-remove" onClick={() => setFilterRole("")}>
                      <i className="fas fa-times" />
                    </button>
                  </span>
                )}
                {filterStatus && (
                  <span className="pbi-chip">
                    <i className="fas fa-user-check" style={{ fontSize: 10 }} />
                    {STATUS_FILTER_OPTIONS.find((o) => o.value === filterStatus)?.label || filterStatus}
                    <button className="pbi-chip-remove" onClick={() => setFilterStatus("")}>
                      <i className="fas fa-times" />
                    </button>
                  </span>
                )}
                <button className="pbi-clear-all" onClick={clearAllFilters}>
                  <i className="fas fa-times-circle" style={{ marginRight: 4 }} />
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* ── Desktop Table ── */}
          <div className="pbi-table-scroll">
            <table className="pbi-table">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>#</th>
                  {[
                    { label: "Name",    field: "name"      },
                    { label: "Email",   field: "email"     },
                    { label: "ID",      field: null        },
                    { label: "Role",    field: "user_role" },
                    { label: "Status",  field: null        },
                    { label: "2FA",     field: null        },
                    { label: "Actions", field: null        },
                  ].map(({ label, field }) => (
                    <th
                      key={label}
                      className={`${field ? "sortable" : ""}${sortField === field ? " sort-active" : ""}`}
                      onClick={() => field && toggleSort(field)}
                    >
                      {label}
                      {field && (
                        <span className="sort-arrow">
                          {sortField === field ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅"}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAgents.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="pbi-empty">
                        <div className="pbi-empty-icon"><i className="fas fa-users" /></div>
                        <div className="pbi-empty-msg">No staff members found.</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredAgents.map((agent, index) => {
                    const name  = agent?.user?.name  || "";
                    const email = agent?.user?.email || "";
                    const role  = agent?.user?.user_role || "";
                    const id    = agent?.user?._id   || "";
                    const roleU = (role || "").toUpperCase();
                    const twoFAEligible = STAFF_2FA_ROLES.includes(roleU);
                    const twoFAOn = !!agent?.user?.twoFA?.enabled;
                    const agentUser = agent?.user || {};
                    const state = lifecycleState(agentUser);
                    const isSelf = String(id) === String(user?.result?._id || "");
                    return (
                      <tr key={id || index} className={state === "ACTIVE" ? "" : "pbi-row-inactive"}>
                        <td style={{ color: "#9aacbf", fontWeight: 700, fontSize: "0.75rem" }}>{index + 1}</td>
                        <td>
                          <div className="pbi-name-cell">
                            <span className="pbi-avatar" style={{ background: getAvatarColor(name) }}>
                              {getInitials(name)}
                            </span>
                            <div>
                              <div className="pbi-name-text">{name || "—"}</div>
                              <div className="pbi-name-sub">Lusuku Staff</div>
                            </div>
                          </div>
                        </td>
                        <td title={email} style={{ color: "#4a5568" }}>{email}</td>
                        <td>
                          <span className="pbi-id-pill" title={id}>
                            {id ? `…${id.slice(-8)}` : "—"}
                          </span>
                        </td>
                        <td><RoleBadge role={role} /></td>
                        <td>
                          <StatusBadge state={state} snapshot={agentUser.staffLifecycle} />
                        </td>
                        <td className="pbi-2fa-cell">
                          <TwoFAStatusBadge enabled={twoFAOn} eligible={twoFAEligible} />
                          {twoFAEligible && (
                            <button
                              type="button"
                              className="pbi-2fa-settings-link"
                              onClick={() => openStaffEdit(id, role)}
                            >
                              2FA settings
                            </button>
                          )}
                        </td>
                        <td>
                          <div className="pbi-actions">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Edit Agent</Tooltip>}>
                              <button
                                type="button"
                                className="pbi-btn pbi-btn-edit"
                                onClick={() => openStaffEdit(id, role)}
                              >
                                <i className="fas fa-edit" />
                              </button>
                            </OverlayTrigger>
                            <LifecycleActions
                              state={state}
                              isSelf={isSelf}
                              canManage={canManageAccess}
                              onAction={(action) => openLifecycle(agentUser, action)}
                              onHistory={() => openHistory(agentUser)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ── Mobile Card List ── */}
          <div className="pbi-card-list">
            {filteredAgents.length === 0 ? (
              <div className="pbi-empty">
                <div className="pbi-empty-icon"><i className="fas fa-users" /></div>
                <div className="pbi-empty-msg">No staff members found.</div>
              </div>
            ) : (
              filteredAgents.map((agent, index) => {
                const name  = agent?.user?.name  || "";
                const email = agent?.user?.email || "";
                const role  = agent?.user?.user_role || "";
                const id    = agent?.user?._id   || "";
                const roleU = (role || "").toUpperCase();
                const twoFAEligible = STAFF_2FA_ROLES.includes(roleU);
                const twoFAOn = !!agent?.user?.twoFA?.enabled;
                const agentUser = agent?.user || {};
                const state = lifecycleState(agentUser);
                const isSelf = String(id) === String(user?.result?._id || "");
                return (
                  <div key={id || index} className="pbi-mobile-card">
                    <span className="pbi-avatar-lg" style={{ background: getAvatarColor(name) }}>
                      {getInitials(name)}
                    </span>
                    <div className="pbi-mobile-card-body">
                      <div className="pbi-mobile-card-name">{name || "—"}</div>
                      <div className="pbi-mobile-card-email">{email || "—"}</div>
                      <div className="pbi-mobile-card-row">
                        <RoleBadge role={role} />
                        <StatusBadge state={state} snapshot={agentUser.staffLifecycle} />
                        <span className="pbi-id-pill" title={id}>
                          {id ? `…${id.slice(-8)}` : "—"}
                        </span>
                      </div>
                      <div className="pbi-mobile-card-row" style={{ marginTop: 8 }}>
                        <TwoFAStatusBadge enabled={twoFAOn} eligible={twoFAEligible} />
                        {twoFAEligible && (
                          <button
                            type="button"
                            className="pbi-2fa-settings-link"
                            style={{ marginTop: 0 }}
                            onClick={() => openStaffEdit(id, role)}
                          >
                            2FA settings
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="pbi-mobile-card-actions">
                      <button
                        type="button"
                        className="pbi-btn pbi-btn-edit"
                        title="Edit Agent"
                        onClick={() => openStaffEdit(id, role)}
                      >
                        <i className="fas fa-edit" />
                      </button>
                      <LifecycleActions
                        state={state}
                        isSelf={isSelf}
                        canManage={canManageAccess}
                        onAction={(action) => openLifecycle(agentUser, action)}
                        onHistory={() => openHistory(agentUser)}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default Users;
